import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import PrivacyConsentForm from 'LoggedOut/SignUp/Forms/PrivacyConsentForm';
import { UserDetails } from 'LoggedOut/SignUp/SignUp';
import { useUserNeedsAdditionalWorkDetails } from 'App/Auth/userNeedsAdditionalWorkDetails/useUserNeedsAdditionalWorkDetails';
import { useConsentsToCollect } from 'LoggedOut/PrivacyConsent/useConsentsToCollect';
import RoutePath from '../../../App/RoutePath';
import { AlertBox } from '../../../Shared/Form';
import LoadingIndicator from '../../../Shared/LoadingIndicator';
import { updateExplicitPrivacyConsent } from './__generated__/updateExplicitPrivacyConsent';

const UPDATE_EXPLICIT_PRIVACY_CONSENT = gql`
  mutation updateExplicitPrivacyConsent($userInput: UpdateUserInput!) {
    updateUser(userInput: $userInput)
  }
`;

const ExplicitPrivacyConsentNeeded = () => {
  const [formError, setFormError] = useState<boolean>();
  const { t: translate } = useTranslation(['logged_out']);
  const { outstandingConsents, firstConsentCapture, isSSOUser, loading } =
    useUserNeedsAdditionalWorkDetails();
  const { shownFields, loading: loadingCheckboxes } = useConsentsToCollect({
    outstandingConsents,
    showMarketingOptIn: firstConsentCapture,
  });

  const [updateExplicitPrivacyConsentMutation] =
    useMutation<updateExplicitPrivacyConsent>(UPDATE_EXPLICIT_PRIVACY_CONSENT, {
      onCompleted: returnedData => {
        // update user returns a boolean indicating success or not
        if (!!returnedData.updateUser) {
          // SSOLoginSuccess records an SSO login unconditionally, so password
          // users would be tracked as having signed in via SSO.
          window.location.assign(
            `${window.location.origin}${
              isSSOUser ? RoutePath.SSOLoginSuccess : RoutePath.Home
            }`,
          );
        }
      },
      onError: () => {
        setFormError(true);
      },
    });

  const onSubmit = async (userDetails: UserDetails) =>
    updateExplicitPrivacyConsentMutation({
      variables: {
        userInput: {
          ...Object.fromEntries(
            shownFields.map(field => [field, userDetails[field] ?? false]),
          ),
          // Only ever written on true, so it cannot clear an existing consent,
          // and an SSO user reaching this screen may never have given it.
          explicitHealthDataConsentGiven: true,
        },
      },
    });

  if (loading || loadingCheckboxes) {
    return <LoadingIndicator />;
  }

  return (
    <>
      <PrivacyConsentForm
        onSubmit={onSubmit}
        outstandingConsents={outstandingConsents}
        showMarketingOptIn={firstConsentCapture}
      />
      {formError ? (
        <AlertBox
          alertType="failed"
          // for now, just add a generic error message
          message={translate('logged_out:login.errors.default_login_error')}
        />
      ) : null}
    </>
  );
};

export default ExplicitPrivacyConsentNeeded;
